import type { SmashSmashed, SmashState } from '@goodtime/shared';
import type { GameView } from './core';

export type SmashView = GameView<SmashState, SmashSmashed>;

/** The object at `index`, if it has been smashed. */
export const smashedAt = (view: SmashView, index: number): SmashSmashed | null =>
  view.state?.smashed.find((s) => s.index === index) ?? null;

/** Whether tapping object `index` smashes it now. */
export const canSmash = (view: SmashView, index: number): boolean =>
  view.phase === 'play' && !smashedAt(view, index);

/** What's in an object, as its label. */
export function smashLabel(s: SmashSmashed): string {
  if (s.kind === 'double') return '×2';
  if (s.kind === 'empty') return 'Empty';
  return `${s.coins.toLocaleString('en-GB')}`;
}

/** The line under the title. */
export function smashLead(view: SmashView, smashes: number): string {
  if (view.phase === 'over') {
    const total = view.prize?.coins ?? 0;
    return total > 0 ? (view.state && view.state.doubles > 0 ? 'Doubled!' : 'Smashing!') : 'No coins this time.';
  }
  return `Smash ${smashes} for coins. A ×2 doubles the lot.`;
}

/** What the counters and the ×2 mean, said on a tap. */
export const SMASH_EXPLAIN = {
  counters: (smashes: number) =>
    `You can smash ${smashes} toadstools. Each holds coins, a ×2 or nothing. Total is what you've found so far, and it's yours when the smashing ends.`,
  double: 'A ×2 doubles every coin you find in this game.',
};

/** The counters under the objects. */
export function smashCounters(view: SmashView, smashes: number): { left: number; total: number } {
  return { left: view.state?.left ?? smashes, total: view.state?.total ?? 0 };
}
